import React, {
  useEffect,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from 'react'
import * as echarts from 'echarts'
import { safeChartInit } from '../../../utils/echarts'
import { Box } from '../../atoms/Box'
import { Text } from '../../atoms/Text'
import { Loading } from '../../atoms/Loading'
import { Button } from '../../atoms/Button'
import { ButtonGroup } from '../../molecules/ButtonGroup'
import { StatsContainer } from '../../molecules/StatsContainer'
import {
  ChartContainer,
  ChartSection,
  ChartGrid,
  ChartTitle,
  ChartLegend,
  ErrorContainer,
  NotAvailable,
  PrintWrapper,
} from '../../molecules/ChartComponents'
import {
  formatPrice,
  formatPricePerSqm,
  getColor,
  getColorSqm,
  calculatePriceThresholds,
  getPriceGeographyLegendItems,
  getPricePerSqmLegendItems,
  processLocationData,
  generateInsights,
} from './PriceGeographyChart.utils'
import {
  PriceGeographyChartProps,
  PriceGeographyChartRef,
  ProcessedLocationData,
  KPIData,
} from './PriceGeographyChart.types'

const PRICE_TYPES = [
  { type: 'median', labelKey: 'shared.median' },
  { type: 'average', labelKey: 'shared.average' },
] as const

export const PriceGeographyChart = forwardRef<
  PriceGeographyChartRef,
  PriceGeographyChartProps
>(
  (
    {
      data,
      loading = false,
      error = null,
      notAvailable = false,
      priceType: controlledPriceType,
      onPriceTypeChange,
      hidePriceTypeButtons = false,
      isRental = false,
      priceMode,
      showInsights = true,
      showControls = true,
      accentColor = '#00ade6',
      t = (key: string) => key,
    },
    ref
  ) => {
    const treemapChartRef = useRef<HTMLDivElement>(null)
    const barChartRef = useRef<HTMLDivElement>(null)
    const sqmChartRef = useRef<HTMLDivElement>(null)
    const [internalPriceType, setInternalPriceType] = useState<
      'median' | 'average'
    >('median')
    const priceType = controlledPriceType ?? internalPriceType

    const treemapInstance = useRef<echarts.ECharts | null>(null)
    const barChartInstance = useRef<echarts.ECharts | null>(null)
    const sqmChartInstance = useRef<echarts.ECharts | null>(null)
    const isPrintingRef = useRef(false)

    // Process data based on aggregation type
    let processedData: ProcessedLocationData[] = []

    if (data.by_canton) {
      processedData = processLocationData(data.by_canton.buckets, 'canton')
    } else if (data.by_district) {
      processedData = processLocationData(data.by_district.buckets, 'district')
    } else if (data.by_municipality) {
      processedData = processLocationData(
        data.by_municipality.buckets,
        'municipality'
      )
    }

    // Sort by selected price type
    processedData.sort((a, b) => {
      const aPrice = priceType === 'median' ? a.medianPrice : a.avgPrice
      const bPrice = priceType === 'median' ? b.medianPrice : b.avgPrice
      return bPrice - aPrice
    })

    // Get national values
    const nationalMedian = data.total_median?.values['50.0'] || 0
    const nationalMedianPerSqm = data.total_median_per_sqm?.values['50.0'] || 0
    const nationalAvg = data.total_avg?.value || 0
    const nationalAvgPerSqm = data.total_avg_per_sqm?.value || 0

    // Generate insights
    const insights = generateInsights(
      processedData,
      priceType,
      priceType === 'median' ? nationalMedian : nationalAvg,
      priceType === 'median' ? nationalMedianPerSqm : nationalAvgPerSqm,
      isRental,
      t
    )

    // Sort processed data by selected price type
    const sortedData = [...processedData].sort((a, b) => {
      const aPrice = priceType === 'median' ? a.medianPrice : a.avgPrice
      const bPrice = priceType === 'median' ? b.medianPrice : b.avgPrice
      return bPrice - aPrice
    })

    // Get highest and lowest for KPIs
    const highest = sortedData[0] || { name: '', medianPrice: 0, avgPrice: 0 }
    const lowest = sortedData[sortedData.length - 1] || {
      name: '',
      medianPrice: 0,
      avgPrice: 0,
    }

    // Prepare KPI data
    const kpiData: KPIData[] = [
      {
        id: 'national-median',
        value: formatPrice(
          priceType === 'median' ? nationalMedian : nationalAvg,
          isRental
        ),
        label:
          priceType === 'median'
            ? t('geography.kpi.nationalMedianPrice')
            : t('geography.kpi.nationalAveragePrice'),
        variant: 'default',
      },
      {
        id: 'national-median-sqm',
        value: formatPricePerSqm(
          priceType === 'median' ? nationalMedianPerSqm : nationalAvgPerSqm,
          isRental,
          priceMode
        ),
        label:
          priceType === 'median'
            ? t('geography.kpi.nationalMedianPriceM2')
            : t('geography.kpi.nationalAveragePriceM2'),
        variant: 'default',
      },
      {
        id: 'highest',
        value: formatPrice(
          priceType === 'median' ? highest.medianPrice : highest.avgPrice,
          isRental
        ),
        label: t('geography.kpi.highest', { name: highest.name }),
        variant: 'default',
      },
      {
        id: 'lowest',
        value: formatPrice(
          priceType === 'median' ? lowest.medianPrice : lowest.avgPrice,
          isRental
        ),
        label: t('geography.kpi.lowest', { name: lowest.name }),
        variant: 'default',
      },
    ]

    // Expose export methods via ref
    useImperativeHandle(ref, () => ({
      exportChart: () => {
        // Export the treemap chart as it's the main visualization
        if (treemapInstance.current) {
          // Force a render to ensure labels are displayed
          treemapInstance.current.resize()

          return treemapInstance.current.getDataURL({
            type: 'png',
            pixelRatio: 3,
            backgroundColor: '#fff',
            excludeComponents: ['toolbox', 'dataZoom'],
          })
        }
        return null
      },
      exportBarChart: () => {
        if (barChartInstance.current) {
          barChartInstance.current.resize()
          return barChartInstance.current.getDataURL({
            type: 'png',
            pixelRatio: 3,
            backgroundColor: '#fff',
            excludeComponents: ['toolbox', 'dataZoom'],
          })
        }
        return null
      },
      exportSqmChart: () => {
        if (sqmChartInstance.current) {
          sqmChartInstance.current.resize()
          return sqmChartInstance.current.getDataURL({
            type: 'png',
            pixelRatio: 3,
            backgroundColor: '#fff',
            excludeComponents: ['toolbox', 'dataZoom'],
          })
        }
        return null
      },
      getLegendData: () => sortedData,
      getSqmLegendData: () => {
        return [...sortedData]
          .filter(
            (d) =>
              (priceType === 'median'
                ? d.medianPricePerSqm
                : d.avgPricePerSqm) > 0
          )
          .sort((a, b) => {
            const aPrice =
              priceType === 'median' ? a.medianPricePerSqm : a.avgPricePerSqm
            const bPrice =
              priceType === 'median' ? b.medianPricePerSqm : b.avgPricePerSqm
            return bPrice - aPrice
          })
      },
      getKPIs: () => kpiData,
      getInsights: () => insights,
    }))

    useEffect(() => {
      if (!loading && !error && !notAvailable && sortedData.length > 0) {
        // Add a small delay to ensure DOM is ready
        const timer = setTimeout(() => {
          createCharts()
        }, 100)

        return () => clearTimeout(timer)
      }

      return () => {
        treemapInstance.current?.dispose()
        barChartInstance.current?.dispose()
        sqmChartInstance.current?.dispose()
      }
    }, [
      sortedData,
      loading,
      error,
      notAvailable,
      priceType,
      isRental,
      priceMode,
    ])

    // Use ResizeObserver to properly resize charts when container size changes
    useEffect(() => {
      const resizeObserver = new ResizeObserver(() => {
        treemapInstance.current?.resize()
        barChartInstance.current?.resize()
        sqmChartInstance.current?.resize()
      })

      if (treemapChartRef.current) {
        resizeObserver.observe(treemapChartRef.current)
      }
      if (barChartRef.current) {
        resizeObserver.observe(barChartRef.current)
      }
      if (sqmChartRef.current) {
        resizeObserver.observe(sqmChartRef.current)
      }

      return () => resizeObserver.disconnect()
    }, [])

    const createCharts = () => {
      if (
        !treemapChartRef.current ||
        !barChartRef.current ||
        !sqmChartRef.current
      )
        return

      // Create Treemap
      if (treemapInstance.current) {
        treemapInstance.current.dispose()
      }

      treemapInstance.current = safeChartInit(
        treemapChartRef.current,
        undefined,
        {
          renderer: 'canvas',
          devicePixelRatio: isPrintingRef.current
            ? 1
            : window.devicePixelRatio || 1,
        }
      )

      // Force resize if in print mode
      if (isPrintingRef.current) {
        setTimeout(() => {
          treemapInstance.current?.resize()
        }, 50)
      }

      // Calculate price thresholds for dynamic coloring
      const prices = sortedData
        .map((d) => (priceType === 'median' ? d.medianPrice : d.avgPrice))
        .filter((p) => p > 0)
      const priceThresholds = calculatePriceThresholds(prices)

      const treemapData = sortedData.map((location) => {
        const price =
          priceType === 'median' ? location.medianPrice : location.avgPrice
        const pricePerSqm =
          priceType === 'median'
            ? location.medianPricePerSqm
            : location.avgPricePerSqm
        return {
          name: location.name,
          value: location.listingCount,
          medianPrice: price,
          medianPricePerSqm: pricePerSqm,
          itemStyle: {
            color: getColor(price, priceThresholds),
          },
          label: {
            show: true,
            formatter: (params: any) => {
              return `${params.name}\n${formatPrice(params.data.medianPrice, isRental, priceMode)}`
            },
            fontSize: location.listingCount > 50 ? 14 : 11,
            color: '#fff',
            fontWeight: 600,
          },
        }
      })

      const treemapOption = {
        backgroundColor: 'transparent',
        tooltip: {
          appendTo: () => document.body,
          formatter: (params: any) => {
            return `
            <div style="padding: 12px;">
              <div style="font-weight: 600; margin-bottom: 8px; font-size: 16px;">${params.name}</div>
              <div style="margin-bottom: 8px;">
                <div style="color: #6c757d; font-size: 12px;">${t('geography.tooltip.price', { type: priceType === 'median' ? t('shared.median') : t('shared.average') })}</div>
                <div style="color: #2563eb; font-size: 20px; font-weight: 700;">
                  ${formatPrice(params.data.medianPrice, isRental, priceMode)}
                </div>
              </div>
              <div style="margin-bottom: 4px;">
                <div style="color: #6c757d; font-size: 12px;">${t('geography.tooltip.priceM2', { type: priceType === 'median' ? t('shared.median') : t('shared.average') })}</div>
                <div style="color: #1a1a1a; font-size: 14px; font-weight: 600;">
                  ${formatPricePerSqm(params.data.medianPricePerSqm || 0, isRental, priceMode)}
                </div>
              </div>
              <div>
                <div style="color: #6c757d; font-size: 12px;">${t('geography.tooltip.numberOfListings')}</div>
                <div style="color: #1a1a1a; font-size: 14px; font-weight: 600;">
                  ${params.value.toLocaleString()}
                </div>
              </div>
            </div>
          `
          },
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderColor: '#e5e7eb',
          borderWidth: 1,
          borderRadius: 8,
          shadowBlur: 10,
          shadowColor: 'rgba(0, 0, 0, 0.1)',
          shadowOffsetX: 0,
          shadowOffsetY: 4,
        },
        series: [
          {
            type: 'treemap',
            data: treemapData,
            left: 0,
            right: 0,
            top: 0,
            bottom: 0,
            roam: false,
            nodeClick: false,
            breadcrumb: {
              show: false,
            },
            levels: [
              {
                itemStyle: {
                  borderColor: '#fff',
                  borderWidth: 3,
                  gapWidth: 3,
                },
              },
            ],
            emphasis: {
              itemStyle: {
                borderColor: '#1e40af',
                borderWidth: 4,
                shadowBlur: 20,
                shadowColor: 'rgba(0, 0, 0, 0.3)',
              },
            },
          },
        ],
      }

      treemapInstance.current.setOption(treemapOption)

      // Create Bar Chart
      if (barChartInstance.current) {
        barChartInstance.current.dispose()
      }
      barChartInstance.current = safeChartInit(barChartRef.current, undefined, {
        renderer: 'canvas',
        devicePixelRatio: isPrintingRef.current
          ? 1
          : window.devicePixelRatio || 1,
      })

      // Force resize if in print mode
      if (isPrintingRef.current) {
        setTimeout(() => {
          barChartInstance.current?.resize()
        }, 50)
      }

      const barOption = {
        backgroundColor: 'transparent',
        tooltip: {
          trigger: 'axis',
          appendTo: () => document.body,
          axisPointer: {
            type: 'shadow',
          },
          formatter: (params: any) => {
            const param = params[0]
            const location = sortedData.find((d) => d.name === param.name)
            return `
            <div style="padding: 12px;">
              <div style="font-weight: 600; margin-bottom: 8px;">${param.name}</div>
              <div style="color: #2563eb; font-size: 18px; font-weight: 600;">
                ${formatPrice(param.value, isRental, priceMode)}
              </div>
              <div style="color: #6c757d; font-size: 12px; margin-top: 4px;">
                ${location?.listingCount.toLocaleString()} listings
              </div>
            </div>
          `
          },
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderColor: '#e5e7eb',
          borderWidth: 1,
          borderRadius: 8,
        },
        grid: {
          left: 10,
          right: 10,
          top: 20,
          bottom: 20,
          containLabel: true,
        },
        xAxis: {
          type: 'value',
          axisLabel: {
            formatter: (value: number) => {
              // Use formatPrice for consistency with rental decimal places
              return formatPrice(value, isRental, priceMode)
            },
            color: '#6c757d',
          },
          axisLine: {
            lineStyle: {
              color: '#e5e7eb',
            },
          },
          splitLine: {
            lineStyle: {
              color: '#f3f4f6',
              type: 'dashed',
            },
          },
          max:
            Math.max(
              ...sortedData.map((d) =>
                priceType === 'median' ? d.medianPrice : d.avgPrice
              )
            ) * 1.1,
        },
        yAxis: {
          type: 'category',
          data: sortedData.map((d) => d.name),
          inverse: true,
          axisLabel: {
            color: '#4b5563',
            fontSize: 11,
          },
          axisLine: {
            lineStyle: {
              color: '#e5e7eb',
            },
          },
          splitLine: {
            show: false,
          },
        },
        series: [
          {
            name: 'Median Price',
            type: 'bar',
            data: (() => {
              // Calculate price thresholds for dynamic coloring
              const prices = sortedData
                .map((d) =>
                  priceType === 'median' ? d.medianPrice : d.avgPrice
                )
                .filter((p) => p > 0)
              const priceThresholds = calculatePriceThresholds(prices)

              return sortedData.map((d) => {
                const price =
                  priceType === 'median' ? d.medianPrice : d.avgPrice
                return {
                  value: price,
                  itemStyle: {
                    color: getColor(price, priceThresholds),
                    borderRadius: [0, 4, 4, 0],
                  },
                }
              })
            })(),
            barWidth: '70%',
            label: {
              show: true,
              position: 'right',
              formatter: (params: any) => {
                return formatPrice(params.value, isRental)
              },
              color: '#4b5563',
              fontSize: 10,
            },
          },
        ],
      }

      barChartInstance.current.setOption(barOption)

      // Create Price per Square Meter Chart
      if (sqmChartInstance.current) {
        sqmChartInstance.current.dispose()
      }
      sqmChartInstance.current = safeChartInit(sqmChartRef.current, undefined, {
        renderer: 'canvas',
        devicePixelRatio: isPrintingRef.current
          ? 1
          : window.devicePixelRatio || 1,
      })

      // Force resize if in print mode
      if (isPrintingRef.current) {
        setTimeout(() => {
          sqmChartInstance.current?.resize()
        }, 50)
      }

      const sqmData = sortedData
        .filter(
          (d) =>
            (priceType === 'median' ? d.medianPricePerSqm : d.avgPricePerSqm) >
            0
        )
        .sort((a, b) => {
          const aPrice =
            priceType === 'median' ? a.medianPricePerSqm : a.avgPricePerSqm
          const bPrice =
            priceType === 'median' ? b.medianPricePerSqm : b.avgPricePerSqm
          return bPrice - aPrice
        })

      // Calculate price per sqm thresholds for dynamic coloring
      const pricesPerSqm = sqmData.map((d) =>
        priceType === 'median' ? d.medianPricePerSqm : d.avgPricePerSqm
      )
      const pricePerSqmThresholds = calculatePriceThresholds(pricesPerSqm)

      const sqmOption = {
        backgroundColor: 'transparent',
        tooltip: {
          trigger: 'axis',
          appendTo: () => document.body,
          axisPointer: {
            type: 'shadow',
          },
          formatter: (params: any) => {
            const param = params[0]
            const location = sqmData.find((d) => d.name === param.name)
            return `
            <div style="padding: 12px;">
              <div style="font-weight: 600; margin-bottom: 8px;">${param.name}</div>
              <div style="color: #8b5cf6; font-size: 18px; font-weight: 600;">
                ${formatPricePerSqm(param.value, isRental, priceMode)}
              </div>
              <div style="color: #6c757d; font-size: 12px; margin-top: 4px;">
                Total ${priceType}: ${formatPrice(location ? (priceType === 'median' ? location.medianPrice : location.avgPrice) : 0, isRental)}
              </div>
            </div>
          `
          },
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderColor: '#e5e7eb',
          borderWidth: 1,
          borderRadius: 8,
        },
        grid: {
          left: 10,
          right: 10,
          top: 20,
          bottom: 20,
          containLabel: true,
        },
        xAxis: {
          type: 'value',
          axisLabel: {
            formatter: (value: number) => {
              return formatPricePerSqm(value, isRental, priceMode)
            },
            color: '#6c757d',
            fontSize: 11,
          },
          axisLine: {
            lineStyle: {
              color: '#e5e7eb',
            },
          },
          splitLine: {
            lineStyle: {
              color: '#f3f4f6',
              type: 'dashed',
            },
          },
        },
        yAxis: {
          type: 'category',
          data: sqmData.map((d) => d.name),
          inverse: true,
          axisLabel: {
            color: '#4b5563',
            fontSize: 11,
          },
          axisLine: {
            lineStyle: {
              color: '#e5e7eb',
            },
          },
          splitLine: {
            show: false,
          },
        },
        series: [
          {
            name: 'Price per m²',
            type: 'bar',
            data: sqmData.map((d) => {
              const pricePerSqm =
                priceType === 'median' ? d.medianPricePerSqm : d.avgPricePerSqm
              return {
                value: pricePerSqm,
                itemStyle: {
                  color: getColorSqm(pricePerSqm, pricePerSqmThresholds),
                  borderRadius: [0, 4, 4, 0],
                },
              }
            }),
            barWidth: '65%',
            label: {
              show: true,
              position: 'right',
              formatter: (params: any) => {
                return formatPricePerSqm(params.value, isRental, priceMode)
              },
              color: '#4b5563',
              fontSize: 10,
            },
          },
        ],
      }

      sqmChartInstance.current.setOption(sqmOption)
    }

    useEffect(() => {
      // Match the Building Permits report, which prints cleanly: do NOT touch
      // the charts on print. The previous beforeprint/afterprint/matchMedia
      // handlers called chart.resize() + setOption() *during* the print
      // snapshot, which made Chrome serialise the canvas as a masked vector
      // figure (the white boxes in macOS Preview / Quick Look). Building
      // Permits charts do nothing on print and rasterise cleanly. The toolbox
      // is already hidden at creation (showToolbox=false in the report) and the
      // print layout keeps the same width, so no print-time resize is needed.
      const handleResize = () => {
        treemapInstance.current?.resize()
        barChartInstance.current?.resize()
        sqmChartInstance.current?.resize()
      }

      window.addEventListener('resize', handleResize)

      return () => {
        window.removeEventListener('resize', handleResize)
      }
    }, [])

    const handlePriceTypeChange = (type: 'median' | 'average') => {
      if (onPriceTypeChange) {
        onPriceTypeChange(type)
      } else {
        setInternalPriceType(type)
      }
    }

    if (loading) {
      return (
        <Box style={{ textAlign: 'center', padding: '32px' }}>
          <Loading variant="spinner" size="large" center />
          <Text variant="body1" color="secondary" style={{ marginTop: '16px' }}>
            {t('geography.loading')}
          </Text>
        </Box>
      )
    }

    if (error) {
      return (
        <ErrorContainer variant="error">
          <Text variant="body1">{error}</Text>
        </ErrorContainer>
      )
    }

    if (notAvailable) {
      return (
        <NotAvailable
          title={t('geography.notAvailable.title')}
          description={t('geography.notAvailable.description')}
        />
      )
    }

    if (data.length === 0) {
      return (
        <ErrorContainer variant="info">
          <Text variant="body1">{t('geography.noData')}</Text>
        </ErrorContainer>
      )
    }

    return (
      <PrintWrapper>
        {showControls && !hidePriceTypeButtons && (
          <Box marginBottom="20px">
            <Box display="flex" justifyContent="flex-end">
              <ButtonGroup>
                {PRICE_TYPES.map((pt) => (
                  <Button
                    key={pt.type}
                    variant={priceType === pt.type ? 'primary' : 'secondary'}
                    size="small"
                    onClick={() => handlePriceTypeChange(pt.type)}
                  >
                    {t(pt.labelKey)}
                  </Button>
                ))}
              </ButtonGroup>
            </Box>
          </Box>
        )}

        <Box marginBottom="24px">
          <StatsContainer
            fullWidth
            stats={[
              {
                id: 'national-median',
                value: formatPrice(
                  priceType === 'median' ? nationalMedian : nationalAvg,
                  isRental
                ),
                label:
                  priceType === 'median'
                    ? t('geography.kpi.nationalMedianPrice')
                    : t('geography.kpi.nationalAveragePrice'),
                variant: 'default',
              },
              {
                id: 'national-median-sqm',
                value: formatPricePerSqm(
                  priceType === 'median'
                    ? nationalMedianPerSqm
                    : nationalAvgPerSqm,
                  isRental,
                  priceMode
                ),
                label:
                  priceType === 'median'
                    ? t('geography.kpi.nationalMedianPriceM2')
                    : t('geography.kpi.nationalAveragePriceM2'),
                variant: 'default',
              },
              {
                id: 'highest',
                value: formatPrice(
                  priceType === 'median'
                    ? highest.medianPrice
                    : highest.avgPrice,
                  isRental
                ),
                label: t('geography.kpi.highest', { name: highest.name }),
                variant: 'default',
              },
              {
                id: 'lowest',
                value: formatPrice(
                  priceType === 'median' ? lowest.medianPrice : lowest.avgPrice,
                  isRental
                ),
                label: t('geography.kpi.lowest', { name: lowest.name }),
                variant: 'default',
              },
            ]}
          />
        </Box>

        <ChartGrid columns={2}>
          <ChartSection
            title={
              priceType === 'median'
                ? t('geography.charts.geoVolumeDistributionMedian')
                : t('geography.charts.geoVolumeDistributionAverage')
            }
          >
            <ChartContainer height={350} ref={treemapChartRef} />
          </ChartSection>
          <ChartSection
            title={
              priceType === 'median'
                ? t('geography.charts.rankingsByMedianPrice')
                : t('geography.charts.rankingsByAveragePrice')
            }
          >
            <ChartContainer height={350} ref={barChartRef} />
          </ChartSection>
        </ChartGrid>

        <ChartGrid columns={2}>
          <ChartSection title={t('geography.charts.rankingsByPricePerSqm')}>
            <ChartContainer
              height={300}
              ref={sqmChartRef}
              style={{
                background: 'white',
                borderRadius: '10px',
              }}
            />
          </ChartSection>
          <ChartSection title={t('geography.charts.priceLegend')}>
            <Box
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                height: '300px',
                overflow: 'auto',
              }}
            >
              <ChartLegend
                items={getPriceGeographyLegendItems(
                  sortedData,
                  priceType,
                  isRental,
                  priceMode,
                  t
                )}
              />
              <ChartLegend
                items={getPricePerSqmLegendItems(
                  sortedData,
                  priceType,
                  isRental,
                  priceMode,
                  t
                )}
              />
            </Box>
          </ChartSection>
        </ChartGrid>

        {showInsights && insights.length > 0 && (
          <Box marginTop="30px">
            <ChartTitle style={{ marginBottom: '24px' }}>
              {t('shared.keyInsights')}
            </ChartTitle>
            <Box display="flex" flexWrap="wrap" gap="20px">
              {insights.map((insight, index) => (
                <Box
                  key={index}
                  flex="1 1 280px"
                  style={{
                    background: '#f8f9fa',
                    borderRadius: '10px',
                    padding: '16px',
                    borderLeft: `4px solid ${accentColor}`,
                    minWidth: 0,
                  }}
                >
                  <Text
                    variant="h4"
                    style={{
                      fontSize: '13px',
                      fontWeight: 600,
                      marginBottom: '6px',
                      color: '#1a1a1a',
                    }}
                  >
                    {insight.title}
                  </Text>
                  <Text
                    variant="body2"
                    style={{
                      fontSize: '12px',
                      lineHeight: 1.5,
                      color: '#6c757d',
                    }}
                  >
                    {insight.description}
                  </Text>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </PrintWrapper>
    )
  }
)

PriceGeographyChart.displayName = 'PriceGeographyChart'
